import { IconButton, MenuItem, Popper, useTheme } from "@mui/material";
import { useProviderSearchContext } from "context/ProviderSearchContext";
import { TRACK_EVENTS } from "core/consts";
import RSButton, {
  RSButtonColor,
  RSButtonVariant,
} from "ds_legacy/components/RSButton";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_16,
  FONT_SIZE_24,
} from "ds_legacy/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import Tabs, { Tab } from "dsl/atoms/Tabs";
import {
  PROVIDER_SEARCH_LOGIN_MODAL,
  PROVIDER_SEARCH_SIGNUP_MODAL,
} from "dsl/ecosystems/ProviderSearchLoginModal/shared";
import { useDropdown, useModal } from "dsl/hooks";
import useFocusWithin from "dsl/hooks/useFocusWithin";
import { useProvidersearchNavigationUrls } from "dsl/hooks/useNavigationHandlers";
import { RegistrationPopover } from "dsl/organisms/ProviderSearchRegistrationDropDown";
import { MenuIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation, useMatch } from "react-router-dom";
import { useTracking } from "react-tracking";
import { useLoggedInAccount } from "reduxentities/hooks";
import styled from "styled-components";
import { useTranslations } from "translations";
import { ConfirmLogoutDialog } from "./LogoutConfirmation";

type NavBarItem = {
  ariaProps?: Record<string, boolean | string>;
  buttonVariant?: RSButtonVariant;
  color?: RSButtonColor;
  isSelected?: boolean;
  key: string;
  onClick: (event?: any) => void;
  show: boolean;
  title: string;
  url?: string;
};

const useGetNavBarActions = ({
  isHamburgerMenu = false,
  onCloseRegistration,
  onOpenLogoutModal,
  onOpenRegistration,
  openRegistration,
}: {
  isHamburgerMenu?: boolean;
  onCloseRegistration?: () => void;
  onOpenLogoutModal: () => void;
  onOpenRegistration?: (event?: any) => void;
  openRegistration?: boolean;
}): NavBarItem[] => {
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const { goToAccount, goToGlossary, goToListing, goToSearchDashboard } =
    useProvidersearchNavigationUrls();
  const account = useLoggedInAccount();
  const { isLoggedIn, patients, setLoginModal } = useProviderSearchContext();

  const patientsAvailable =
    patients.active.concat(patients.archived).length > 0;
  const onboardingSelected = useMatch("providersearch/:provider_search");

  return [
    {
      isSelected: !!onboardingSelected,
      key: "listing",
      onClick: () => {
        trackEvent({
          name: TRACK_EVENTS.PROVIDER_SEARCH_MAIN_MENU_HOMEPAGE_CLICKED,
        });
      },
      show: true,
      title: translations.providersearch.header.homePage,
      url: goToListing(),
    },
    {
      key: "glossary",
      onClick: () => {
        trackEvent({
          name: TRACK_EVENTS.PROVIDER_SEARCH_MAIN_MENU_GLOSSARY_CLICKED,
        });
      },
      show: true,
      title: translations.providersearch.header.glossary,
      url: goToGlossary(),
    },
    {
      key: "patient",
      onClick: () => {
        trackEvent({
          name: TRACK_EVENTS.PROVIDER_SEARCH_MAIN_MENU_SEARCHES_CLICKED,
        });
      },
      show: isLoggedIn && patientsAvailable,
      title: translations.providersearch.header.requestDashboard,
      url: goToSearchDashboard(),
    },
    {
      ariaProps: {
        "aria-haspopup": "dialog",
      },
      key: "login",
      onClick: () => {
        setLoginModal(PROVIDER_SEARCH_LOGIN_MODAL);
      },
      show: !isLoggedIn && isHamburgerMenu,
      title: translations.providersearch.loginDropDown.loginButton,
    },
    {
      ariaProps: {
        "aria-haspopup": "dialog",
      },
      key: "createAccount",
      onClick: () => {
        setLoginModal(PROVIDER_SEARCH_SIGNUP_MODAL);
      },
      show: !isLoggedIn && isHamburgerMenu,
      title: translations.providersearch.loginDropDown.createAccountButton,
    },
    {
      key: "account",
      onClick: () => {
        if (account?.id) {
          trackEvent({
            name: TRACK_EVENTS.PROVIDER_SEARCH_MAIN_MENU_MY_ACCOUNT_CLICKED,
          });
        }
      },
      show: isLoggedIn,
      title: translations.providersearch.header.myAccount,
      url: account?.id ? goToAccount({ accountId: account.id }) : "",
    },
    {
      ariaProps: {
        "aria-haspopup": "dialog",
      },
      buttonVariant: "contained",
      color: "primary",
      key: "register",
      onClick: (e) => {
        if (openRegistration) {
          onCloseRegistration?.();
        } else {
          onOpenRegistration?.(e);
        }
      },
      show: !isLoggedIn && !isHamburgerMenu,
      title: translations.providersearch.header.register,
    },
    {
      ariaProps: {
        "aria-haspopup": "dialog",
      },
      buttonVariant: "outlined",
      color: "primary",
      key: "logout",
      onClick: () => {
        onOpenLogoutModal();
      },
      show: isLoggedIn,
      title: translations.providersearch.header.logout,
    },
  ];
};

const StyledListItem = styled.li`
  display: flex;
  flex: 1 1 auto;
`;

function NavigationActions({
  onOpenLogoutModal,
}: {
  onOpenLogoutModal: () => void;
}) {
  const { anchor, onClose, onOpen, open } = useDropdown();

  const actionItems = useGetNavBarActions({
    onCloseRegistration: onClose,
    onOpenLogoutModal,
    onOpenRegistration: onOpen,
    openRegistration: open,
  });

  return (
    <>
      <Tabs
        containerAs="ul"
        containerStyle={{ listStyleType: "none", margin: 0, padding: 0 }}
        height="var(--app-bar-providersearch-height)"
        isFit
        tabsContainerPadding={padding(1 / 8, 0, 0, 0)}
      >
        {actionItems
          .filter((item) => item.show && item.url)
          .map((itemConfig) => {
            return (
              <StyledListItem key={itemConfig.key}>
                <Tab
                  fontSize={FONT_SIZE_16}
                  isPrimary
                  isSelected={itemConfig.isSelected}
                  isUppercase={false}
                  label={itemConfig.title}
                  match={itemConfig.key}
                  shouldDisableTouchRipple
                  tabMargin={margin(0, 1.5)}
                  testId={itemConfig.key}
                  url={itemConfig.url}
                  onClick={itemConfig.onClick}
                />
              </StyledListItem>
            );
          })}
      </Tabs>
      {actionItems
        .filter((item) => item.show && !item.url)
        .map((itemConfig) => {
          return (
            <RSButton
              key={itemConfig.key}
              ariaProps={itemConfig.ariaProps}
              color={itemConfig.color}
              id={itemConfig.key}
              loading="na"
              variant={itemConfig.buttonVariant ?? "text"}
              onClick={itemConfig.onClick}
            >
              {itemConfig.title}
            </RSButton>
          );
        })}

      <RegistrationPopover anchor={anchor} isOpen={open} onClose={onClose} />
    </>
  );
}

const OverlayList = styled.ul`
  background-color: #fff;
  border-radius: ${dp(4)};
  box-shadow:
    ${dp(0)} ${dp(5)} ${dp(5)} ${dp(-3)} rgba(0, 0, 0, 0.2),
    ${dp(0)} ${dp(8)} ${dp(10)} ${dp(1)} rgba(0, 0, 0, 0.14),
    ${dp(0)} ${dp(3)} ${dp(14)} ${dp(2)} rgba(0, 0, 0, 0.12);
`;

const menuItemStyles = `
  border: 0;
  margin: 0;
  outline: 0;
  width: 100%;
  height: 100%;
  padding: ${padding(0.75, 2)};
`;

const MenuLink = styled(Link)`
  ${menuItemStyles}
  text-decoration: none;
`;

const MenuButton = styled.button`
  ${menuItemStyles}
  background-color: transparent;
  text-align: left;
  cursor: pointer;
`;

const MenuLabel = ({ label }: { label: string }) => (
  <Caption fontSize={FONT_SIZE_16} lineHeight={FONT_SIZE_24} margin={margin(0)}>
    {label}
  </Caption>
);

function DropdownMenu({
  anchor,
  id,
  isOpen,
  onClose,
  onOpenLogoutModal,
}: {
  anchor: Element | null;
  id: string;
  isOpen: boolean;
  onClose: () => void;
  onOpenLogoutModal: () => void;
}) {
  const menuItems = useGetNavBarActions({
    isHamburgerMenu: true,
    onOpenLogoutModal,
  });

  const location = useLocation();

  return (
    <Popper
      anchorEl={anchor}
      components={{ Root: OverlayList }}
      componentsProps={{ root: { role: undefined } }}
      disablePortal
      id={id}
      open={isOpen}
      placement="bottom-end"
      sx={{ padding: padding(1, 0) }}
    >
      {menuItems
        .filter((item) => item.show)
        .map((item) => {
          const keyRegex = new RegExp(
            item.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
          );
          const selected = keyRegex.test(location.pathname);

          const handleClick = () => {
            item.onClick();
            onClose();
          };

          return (
            <MenuItem
              key={item.key}
              data-testid={item.key}
              role="listitem"
              selected={selected}
              sx={{ padding: 0 }}
            >
              {item.url ? (
                <MenuLink
                  aria-current={selected ? "page" : undefined}
                  to={item.url}
                  onClick={handleClick}
                >
                  <MenuLabel label={item.title} />
                </MenuLink>
              ) : (
                <MenuButton onClick={handleClick} {...item.ariaProps}>
                  <MenuLabel label={item.title} />
                </MenuButton>
              )}
            </MenuItem>
          );
        })}
    </Popper>
  );
}

function HamburgerMenu({
  onOpenLogoutModal,
}: {
  onOpenLogoutModal: () => void;
}) {
  const translations = useTranslations();
  const theme = useTheme();
  const { anchor, onClose, onOpen, open } = useDropdown();
  const { isFocusWithin, parentRef } = useFocusWithin<HTMLSpanElement>();

  useEffect(() => {
    if (!isFocusWithin && open) {
      onClose();
    }
  }, [isFocusWithin, open]);

  const menuId = "hamburger-navigation-menu";

  return (
    <span ref={parentRef}>
      <IconButton
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-label={
          translations.providersearch.searchDashboard.accessibility.menuLabel
        }
        data-testid="hamburger"
        onClick={open ? onClose : onOpen}
      >
        <MenuIcon
          size={FONT_SIZE_16}
          style={{ color: theme.palette.neutrals.base_dark }}
        />
      </IconButton>
      <DropdownMenu
        anchor={anchor}
        id={menuId}
        isOpen={open}
        onClose={onClose}
        onOpenLogoutModal={onOpenLogoutModal}
      />
    </span>
  );
}

export const NavBarActions = () => {
  const { isTablet } = useMedia();
  const [logoutModalOpen, setLogoutModalOpen] = useModal();

  return (
    <>
      {isTablet ? (
        <HamburgerMenu onOpenLogoutModal={() => setLogoutModalOpen(true)} />
      ) : (
        <NavigationActions onOpenLogoutModal={() => setLogoutModalOpen(true)} />
      )}
      {logoutModalOpen && (
        <ConfirmLogoutDialog onClose={() => setLogoutModalOpen(false)} />
      )}
    </>
  );
};
